import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetch } from '../common/authFetch';
import ConfirmationDialog from '../ConfirmationDialog';
import '../css/Announcements.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AnnouncementManager() {
    const { t } = useTranslation();
    const [announcements, setAnnouncements] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({
        title: '', content: '', display_type: 'post',
        popup_image_url: '', popup_text: '',
        is_pinned: false, is_active: true
    });
    const [uploading, setUploading] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

    const fetchAnnouncements = useCallback(async () => {
        try {
            const res = await authFetch(`${API_URL}/api/v1/admin/announcements`);
            const data = await res.json();
            if (data.success) setAnnouncements(data.announcements);
        } catch (e) { console.error(e); }
    }, []);

    useEffect(() => { fetchAnnouncements(); }, [fetchAnnouncements]);

    const resetForm = () => {
        setEditing(null);
        setForm({
            title: '', content: '', display_type: 'post',
            popup_image_url: '', popup_text: '',
            is_pinned: false, is_active: true
        });
    };

    const handleEdit = (ann) => {
        setEditing(ann.announcement_id);
        setForm({
            title: ann.title,
            content: ann.content || '',
            display_type: ann.display_type,
            popup_image_url: ann.popup_image_url || '',
            popup_text: ann.popup_text || '',
            is_pinned: ann.is_pinned,
            is_active: ann.is_active
        });
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('image', file);
            const res = await authFetch(`${API_URL}/api/v1/admin/announcements/upload-image`, {
                method: 'POST', body: formData
            });
            const data = await res.json();
            if (data.success) {
                setForm(prev => ({ ...prev, popup_image_url: data.imageUrl }));
            }
        } catch (e) { console.error(e); }
        setUploading(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) return;

        try {
            const url = editing
                ? `${API_URL}/api/v1/admin/announcements/${editing}`
                : `${API_URL}/api/v1/admin/announcements`;
            const res = await authFetch(url, {
                method: editing ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form)
            });
            const data = await res.json();
            if (data.success) {
                resetForm();
                fetchAnnouncements();
            }
        } catch (e) { console.error(e); }
    };

    const handleDelete = (id) => {
        setConfirmDialog({
            isOpen: true,
            title: t('confirm'),
            message: t('announcement.admin.deleteConfirm'),
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                try {
                    const res = await authFetch(`${API_URL}/api/v1/admin/announcements/${id}`, { method: 'DELETE' });
                    const data = await res.json();
                    if (data.success) fetchAnnouncements();
                } catch (e) { console.error(e); }
            }
        });
    };

    const handleToggleActive = async (ann) => {
        try {
            const res = await authFetch(`${API_URL}/api/v1/admin/announcements/${ann.announcement_id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ is_active: !ann.is_active })
            });
            const data = await res.json();
            if (data.success) fetchAnnouncements();
        } catch (e) { console.error(e); }
    };

    return (
        <div className="admin-section">
            <h3>{editing ? t('announcement.admin.edit') : t('announcement.admin.create')}</h3>

            <form className="ann-form" onSubmit={handleSubmit}>
                <div className="ann-form-row">
                    <label>{t('announcement.admin.title')}</label>
                    <input
                        type="text"
                        value={form.title}
                        onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
                        required
                    />
                </div>

                <div className="ann-form-row">
                    <label>{t('announcement.admin.displayType')}</label>
                    <select
                        value={form.display_type}
                        onChange={e => setForm(prev => ({ ...prev, display_type: e.target.value }))}
                    >
                        <option value="post">{t('announcement.admin.typePost')}</option>
                        <option value="popup">{t('announcement.admin.typePopup')}</option>
                    </select>
                </div>

                <div className="ann-form-row">
                    <label>{t('announcement.admin.content')}</label>
                    <textarea
                        value={form.content}
                        onChange={e => setForm(prev => ({ ...prev, content: e.target.value }))}
                        rows={6}
                    />
                </div>

                {form.display_type === 'popup' && (
                    <>
                        <div className="ann-form-row">
                            <label>{t('announcement.admin.popupImage')}</label>
                            <div className="ann-image-upload">
                                <input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} />
                                {uploading && <span className="ann-uploading">{t('announcement.admin.uploading')}</span>}
                            </div>
                            {form.popup_image_url && (
                                <div className="ann-image-preview">
                                    <img src={`${API_URL}${form.popup_image_url}`} alt="preview" />
                                    <button type="button" className="ann-remove-image" onClick={() => setForm(prev => ({ ...prev, popup_image_url: '' }))}>
                                        {'\u2715'}
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="ann-form-row">
                            <label>{t('announcement.admin.popupText')}</label>
                            <textarea
                                value={form.popup_text}
                                onChange={e => setForm(prev => ({ ...prev, popup_text: e.target.value }))}
                                rows={3}
                            />
                        </div>
                    </>
                )}

                <div className="ann-form-row ann-form-checks">
                    <label className="ann-checkbox-label">
                        <input
                            type="checkbox"
                            checked={form.is_pinned}
                            onChange={e => setForm(prev => ({ ...prev, is_pinned: e.target.checked }))}
                        />
                        {t('announcement.admin.pinned')}
                    </label>
                    <label className="ann-checkbox-label">
                        <input
                            type="checkbox"
                            checked={form.is_active}
                            onChange={e => setForm(prev => ({ ...prev, is_active: e.target.checked }))}
                        />
                        {t('announcement.admin.active')}
                    </label>
                </div>

                <div className="ann-form-actions">
                    <button type="submit" className="ann-btn ann-btn-primary">
                        {editing ? t('announcement.admin.update') : t('announcement.admin.create')}
                    </button>
                    {editing && (
                        <button type="button" className="ann-btn ann-btn-secondary" onClick={resetForm}>
                            {t('cancel')}
                        </button>
                    )}
                </div>
            </form>

            <h3 style={{ marginTop: '2rem' }}>{t('announcement.admin.list')}</h3>

            {announcements.length === 0 ? (
                <p className="ann-empty">{t('announcement.admin.noAnnouncements')}</p>
            ) : (
                <div className="ann-admin-list">
                    {announcements.map(ann => (
                        <div key={ann.announcement_id} className={`ann-admin-item ${!ann.is_active ? 'ann-inactive' : ''}`}>
                            <div className="ann-admin-item-header">
                                <div className="ann-admin-item-title">
                                    {ann.is_pinned && <span className="ann-pin-badge">{'\uD83D\uDCCC'}</span>}
                                    <span className={`ann-type-badge ann-type-${ann.display_type}`}>
                                        {t(`announcement.admin.type${ann.display_type === 'post' ? 'Post' : 'Popup'}`)}
                                    </span>
                                    <span>{ann.title}</span>
                                </div>
                                <div className="ann-admin-item-meta">
                                    <span className={`status-badge ${ann.is_active ? 'active' : 'inactive'}`}>
                                        {ann.is_active ? t('announcement.admin.active') : t('announcement.admin.inactive')}
                                    </span>
                                    <span className="ann-date">{new Date(ann.created_at).toLocaleDateString()}</span>
                                </div>
                            </div>
                            <div className="ann-admin-item-actions">
                                <button className="ann-btn ann-btn-small" onClick={() => handleToggleActive(ann)}>
                                    {ann.is_active ? t('announcement.admin.deactivate') : t('announcement.admin.activate')}
                                </button>
                                <button className="ann-btn ann-btn-small" onClick={() => handleEdit(ann)}>
                                    {t('announcement.admin.editBtn')}
                                </button>
                                <button className="ann-btn ann-btn-small ann-btn-danger" onClick={() => handleDelete(ann.announcement_id)}>
                                    {t('delete')}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ConfirmationDialog
                isOpen={confirmDialog.isOpen}
                title={confirmDialog.title}
                message={confirmDialog.message}
                onConfirm={confirmDialog.onConfirm}
                onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
                confirmLabel={t('delete')}
                isDanger
            />
        </div>
    );
}

export default AnnouncementManager;
